import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";

export interface BillingConfig {
  id: string;
  organization_id: string;
  default_payment_terms_days: number;
  default_vat_rate: number;
  company_name: string | null;
  company_org_number: string | null;
  company_address: string | null;
  company_email: string | null;
  company_phone: string | null;
  company_bankgiro: string | null;
  company_swish: string | null;
  fortnox_enabled: boolean;
  payment_deadline_mode: string;
  payment_deadline_hours: number;
  require_full_payment_at_booking: boolean;
  payment_reminder_hours: string;
  guarantor_charge_enabled: boolean;
  grace_period_hours: number;
  auto_cancel_unpaid: boolean;
  default_credit_validity_days: number;
}

const DEFAULTS: Omit<BillingConfig, "id" | "organization_id"> = {
  default_payment_terms_days: 30,
  default_vat_rate: 25,
  company_name: null,
  company_org_number: null,
  company_address: null,
  company_email: null,
  company_phone: null,
  company_bankgiro: null,
  company_swish: null,
  fortnox_enabled: false,
  payment_deadline_mode: "none",
  payment_deadline_hours: 2,
  require_full_payment_at_booking: false,
  payment_reminder_hours: "24,6,1",
  guarantor_charge_enabled: false,
  grace_period_hours: 2,
  auto_cancel_unpaid: false,
  default_credit_validity_days: 0,
};

export function useBillingConfig() {
  const { organization } = useOrganization();
  const queryClient = useQueryClient();

  const { data: config, isLoading } = useQuery({
    queryKey: ["billing-config", organization?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("billing_config")
        .select("*")
        .eq("organization_id", organization!.id)
        .maybeSingle();
      if (error) throw error;
      if (data) return data as unknown as BillingConfig;
      const { data: created, error: createErr } = await supabase
        .from("billing_config")
        .insert({ organization_id: organization!.id })
        .select()
        .single();
      if (createErr) throw createErr;
      return created as unknown as BillingConfig;
    },
    enabled: !!organization?.id,
  });

  const updateConfig = useMutation({
    mutationFn: async (updates: Partial<BillingConfig>) => {
      if (!config?.id) throw new Error("No billing config");
      const { error } = await supabase
        .from("billing_config")
        .update(updates as any)
        .eq("id", config.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["billing-config"] });
    },
  });

  const effectiveConfig = useMemo(
    () => config ? config : { ...DEFAULTS, id: "", organization_id: organization?.id ?? "" } as BillingConfig,
    [config, organization?.id]
  );

  return { config: effectiveConfig, isLoading, updateConfig };
}
